import type { User } from "../types";

/**
 * Guide d'utilisation client (page /help).
 *
 * Contenu bilingue FR/EN stocké ici (hors locales JSON) pour ne pas gonfler
 * les fichiers i18n. La parité FR/EN est garantie par `tests/client-guide.test.ts`.
 * Les libellés d'interface (titre de page, « Sommaire », états vides) restent
 * dans `src/i18n/locales/{fr,en}.json` sous la clé `clientGuide`.
 *
 * Chaque section porte la liste des profils clients qui la voient. Les profils
 * internes KESTIO (ADM/CSL/COO) voient TOUTES les sections (support).
 * L'ordre suit le parcours d'onboarding client :
 *   prise en main → créer le contenu → constituer l'équipe → s'entraîner →
 *   animer (challenges) → suivre (badges/historique/reporting) → gérer (facturation).
 */

export type ProfileType = User["profileType"];

/** Profils internes KESTIO — voient l'intégralité du guide. */
const INTERNAL_PROFILES: ProfileType[] = ["ADM", "CSL", "COO"];

/** Tous les profils clients (raccourci pour les sections « tout le monde »). */
const ALL_CLIENTS: ProfileType[] = ["ORG", "MNG", "GES", "RH", "BNF"];

export interface GuideSection {
  id: string;
  /** Nom d'icône lucide-react, résolu dans HelpPage. */
  icon: string;
  /** Profils clients autorisés à voir la section. */
  allowedProfiles: ProfileType[];
  title: { fr: string; en: string };
  /** Corps en markdown léger (## ### **gras** *italique* listes `-`). */
  body: { fr: string; en: string };
}

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: "prise-en-main",
    icon: "Compass",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Prise en main", en: "Getting started" },
    body: {
      fr: `## Bienvenue sur BizCoach

BizCoach est la plateforme d'**entraînement commercial par simulation IA** de KESTIO. Chacun s'exerce face à des **simulateurs vocaux** (vente, négociation, service client…) qui jouent le rôle de vos clients, puis reçoit un **feedback détaillé et un score**. On peut aussi **préparer et débriefer ses vrais rendez-vous** dans l'espace **Terrain**.

## Deux vues : Play & Pilot

- **Play** (vue opérationnelle) — pour s'entraîner : **Accueil · Entraînement · Terrain · Progrès**.
- **Pilot** (managers & organisateurs) — pour encadrer : **Piloter · Planifier · Concevoir**. L'interrupteur **Play / Pilot** en haut permet de basculer ; les managers/organisateurs y arrivent par défaut.

## Votre accueil — « Cette semaine »

Le bloc **« Mes sessions »** vous propose une courte file de ce qu'il y a à faire — sessions d'équipe, évaluation, parties à rejouer — chaque ligne dit d'où elle vient et se lance d'un clic. Chaque session faite rapporte des **BizCoins**.

## Le parcours en bref

- **Concevoir** *(Pilot)* : offres, personae, **cas de simulation**, contextes, critères.
- **Constituer l'équipe** : inviter des **collaborateurs**, attribuer des **licences**.
- **S'entraîner** *(Play)* : sessions vocales sur les simulateurs + **Terrain** (vrais RDV).
- **Planifier** *(Pilot)* : le **plan d'entraînement** et un onglet par type — **Campagnes**, **Challenges**, **Tournois**, **Parcours** ; évaluations et sessions se posent dans le plan.
- **Piloter** *(Pilot)* : le cockpit du lundi, l'**Activité** et la **Progression** de l'équipe, la fiche de chacun.

## Trouver de l'aide

- Le bouton **?** (en haut à droite) ouvre ce **guide écrit** et propose des **visites guidées interactives** : le guide vous emmène sur chaque écran et surligne les boutons, pas à pas.
- Dans ce guide écrit, un bouton **« ▶ Visite guidée »** sur une rubrique lance la visite correspondante.
- Le bouton de **chat** (en bas à droite) vous met en relation avec l'équipe KESTIO.`,
      en: `## Welcome to BizCoach

BizCoach is KESTIO's **AI-powered sales training platform**. Everyone practices against **voice simulators** (sales, negotiation, customer service…) that play the role of your clients, then receives **detailed feedback and a score**. You can also **prepare and debrief your real meetings** in the **Field** space.

## Two views: Play & Pilot

- **Play** (operational view) — to train: **Home · Training · Field · Progress**.
- **Pilot** (managers & organizers) — to coach: **Steer · Planning · Build**. The **Play / Pilot** switch at the top toggles between them; managers/organizers land here by default.

## Your home — "This week"

The **"My sessions"** block offers a short list of what to do — team sessions, assessment, games to replay — each line says where it comes from and starts in one click. Each session completed earns **BizCoins**.

## The journey at a glance

- **Build** *(Pilot)*: offers, personas, **simulation cases**, contexts, criteria.
- **Build the team**: invite **collaborators**, assign **licences**.
- **Train** *(Play)*: voice sessions on the simulators + **Field** (real meetings).
- **Planning** *(Pilot)*: the **training plan** and one tab per type — **Campaigns**, **Challenges**, **Tournaments**, **Paths**; assessments and sessions are placed in the plan.
- **Steer** *(Pilot)*: the Monday cockpit, the team's **Activity** and **Progress**, each person's profile.

## Getting help

- The **?** button (top right) opens this **written guide** and offers **interactive guided tours**: the guide takes you to each screen and highlights the buttons, step by step.
- In this written guide, a **"▶ Guided tour"** button on a section launches the matching tour.
- The **chat** button (bottom right) connects you with the KESTIO team.`,
    },
  },
  {
    id: "creer-cas-simulation",
    icon: "Target",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Créer un cas de simulation", en: "Create a simulation case" },
    body: {
      fr: `## C'est quoi ?

Un **cas de simulation** est le scénario concret sur lequel vos équipes s'entraînent. C'est la pièce centrale de BizCoach : il combine trois éléments qui déterminent le comportement du client IA :

- une **offre** — le produit ou service à vendre / défendre ;
- un **persona** — le profil du client incarné par l'IA (fonction, personnalité, objections…) ;
- un **contexte** — la situation de l'échange (prise de RDV, closing, réclamation…).

## À quoi ça sert ?

Le cas rend l'entraînement **réaliste et ciblé** : au lieu d'un exercice générique, vos commerciaux s'exercent sur **vos** offres, face à **vos** typologies de clients, dans **vos** situations. Un même cas peut être rejoué autant de fois que voulu et sert aussi de support aux **challenges**.

## Où le trouver ?

Cliquez sur votre **photo de profil** (en haut à droite) → **Paramètres** → onglet **Cas de simulation**. Vous y voyez la liste de vos cas, avec pour chacun le nombre de sessions jouées et le score moyen.

## Comment en créer un — pas à pas

1. Onglet **Cas de simulation**, bouton **« Nouveau cas »** : l'assistant guidé s'ouvre.
2. Choisissez l'**offre** concernée (ou créez-en une à la volée).
3. Choisissez le **persona** (ou créez-le) — c'est lui qui donnera le ton du client IA.
4. Précisez le **contexte** et le **niveau de difficulté** (facile / moyen / difficile).
5. **Enregistrez** : le cas est immédiatement disponible pour l'entraînement et les challenges.

> Astuce : plus votre offre et votre persona sont détaillés, plus le simulateur IA sera crédible. Les sections **Créer vos offres** et **Créer vos personae** détaillent chaque champ.`,
      en: `## What is it?

A **simulation case** is the concrete scenario your teams train on. It is the centrepiece of BizCoach: it combines three elements that shape the AI client's behaviour:

- an **offer** — the product or service to sell / defend;
- a **persona** — the client profile played by the AI (role, personality, objections…);
- a **context** — the situation of the exchange (booking a meeting, closing, complaint…).

## What is it for?

The case makes training **realistic and targeted**: instead of a generic exercise, your reps practise on **your** offers, facing **your** client types, in **your** situations. A case can be replayed as often as needed and also powers **challenges**.

## Where to find it

Click your **profile picture** (top right) → **Settings** → **Simulation cases** tab. You'll see the list of your cases, each with the number of sessions played and the average score.

## How to create one — step by step

1. **Simulation cases** tab, **"New case"** button: the guided wizard opens.
2. Pick the relevant **offer** (or create one on the fly).
3. Pick the **persona** (or create it) — it sets the tone of the AI client.
4. Set the **context** and the **difficulty level** (easy / medium / hard).
5. **Save**: the case is immediately available for training and challenges.

> Tip: the more detailed your offer and persona, the more believable the AI simulator. The **Create your offers** and **Create your personas** sections detail every field.`,
    },
  },
  {
    id: "cockpit",
    icon: "Compass",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Le cockpit", en: "The cockpit" },
    body: {
      fr: `## Ce qui appelle une décision cette semaine

Le **Cockpit** est votre premier écran en espace Pilot. Il ne montre pas tout ce
qui s'est passé — il montre ce sur quoi vous devez agir.

- **À traiter** : les situations repérées automatiquement. Quelqu'un n'a jamais
  démarré, un parcours assigné n'a pas été ouvert, une compétence décroche.
  Chaque alerte porte les prénoms concernés et un bouton qui mène à l'action.
- **Cette semaine** : ce qu'il y a à célébrer. Une certification obtenue, une
  première session, une régularité tenue.
- **La matrice d'équipe** : qui progresse, qui a besoin d'aide. Les personnes
  dont le niveau n'a pas encore été mesuré ne sont jamais classées — l'absence
  de mesure n'est pas un mauvais résultat.

## Le comment

1. Ouvrez **Pilot › Piloter › Cockpit**.
2. Lisez « À traiter » : chaque ligne dit quoi faire, pour qui, et pourquoi.
3. Cliquez le bouton de l'alerte — il ouvre l'écran où l'action se fait, avec
   les personnes déjà pré-sélectionnées.

> Les alertes sont paramétrables par Kestio : leur libellé, leur bouton et leur
> destination s'ajustent sans mise à jour de l'application.`,
      en: `## What calls for a decision this week

The **Cockpit** is your first screen in Pilot. It doesn't show everything that
happened — it shows what you need to act on.

- **To handle**: situations detected automatically. Someone never started, an
  assigned path was never opened, a skill is slipping. Each alert carries the
  names involved and a button that leads to the action.
- **This week**: what deserves celebrating. A certification earned, a first
  session, a streak held.
- **The team matrix**: who is progressing, who needs help. People whose level
  hasn't been measured yet are never ranked — a missing measure is not a poor
  result.

## How to

1. Open **Pilot › Steer › Cockpit**.
2. Read "To handle": each line says what to do, for whom, and why.
3. Click the alert's button — it opens the screen where the action happens,
   with the right people already selected.

> Alerts are configurable by Kestio: their wording, button and destination can
> be adjusted without an application update.`,
    },
  },
  {
    id: "plan",
    icon: "CalendarDays",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Poser le plan d'entraînement", en: "Setting up the training plan" },
    body: {
      fr: `## Le plan, c'est le calendrier de l'équipe

Comme un plan marketing, le **plan d'entraînement** met dans le temps tout ce
qui fait progresser l'équipe : **campagnes** (l'enjeu, le périmètre, l'objectif
chiffré), **challenges**, **parcours**, **évaluations** et **sessions**. Il n'y a
rien à créer pour l'avoir : ouvrez **Pilot › Planifier › Plan**, il est là —
vide au début, prêt à être rempli.

Quatre échelles (**Annuel · Trimestre · Mois · Semaine**), trois lectures (**par
simulateur**, **par commercial**, **par équipe**), une couleur par type d'action.
Le bouton **« ? Comprendre le plan »** explique chaque type et
son cycle de vie.

## Quatre façons d'ajouter

1. **Ajouter au plan** — le menu propose chaque type, pré-rempli avec la
   période affichée.
2. **Tracer** sur une ligne vide : la ligne donne le simulateur (ou la
   personne), le tracé donne les dates. « Créer » pose l'élément, « Configurer… »
   ouvre l'écran complet.
3. **Depuis une campagne** (panneau latéral) : ajouter un challenge, un parcours,
   planifier les sessions — tout hérite de la campagne.
4. **Construire mon plan** : cochez vos enjeux, les campagnes sont réparties sur
   les six mois à venir, en brouillon.

## Le temps travaille pour vous

- Une campagne complète **part seule** à sa date de début et **se clôt seule** à
  sa date de fin, avec sa photo avant / après et un verdict calculé.
- **Glissez** une barre pour la décaler, **tirez** son bord droit pour la
  prolonger ; les éléments rattachés suivent. Un clic « Annuler » si besoin.
- **Préparer** monte tout ce qui se déduit (existant relié, cas et challenge
  créés, sessions posées) ; avec « Lancement automatique » cochée, la campagne
  part seule à sa date. **Lancer la campagne** montre d'abord ce qui va se
  passer (qui peut jouer, début avancé) : vous choisissez « Lancer » ou
  « Annuler », puis un compte rendu dit qui est inscrit et qui est écarté.

> Le cockpit ouvre sur **Cette semaine dans le plan** : la campagne en cours,
> qui n'a pas encore joué (Relancer), qui a progressé (Féliciter).`,
      en: `## The plan is the team's calendar

Like a marketing plan, the **training plan** puts everything that makes the team
progress on a timeline: **campaigns** (the goal, the scope, the measurable
target), **challenges**, **paths**, **assessments** and **sessions**. There is
nothing to create to have one: open **Pilot › Planning › Plan** and it is there —
empty at first, ready to be filled.

Four scales (**Year · Quarter · Month · Week**), three readings (**by
simulator**, **by rep**, **by team**), one colour per action type. The **“?
Understand the plan”** button explains each type and its life cycle.

## Four ways to add

1. **Add to plan** — the menu offers every type, pre-filled with the displayed
   period.
2. **Draw** on an empty row: the row gives the simulator (or the person), the
   drawing gives the dates. “Create” places the item, “Configure…” opens the
   full screen.
3. **From a campaign** (side panel): add a challenge, a path, schedule
   sessions — everything inherits from the campaign.
4. **Build my plan**: tick your priorities, campaigns are spread over the next
   six months, as drafts.

## Time works for you

- A complete campaign **starts on its own** at its start date and **closes on
  its own** at its end date, with its before / after snapshot and a computed
  outcome.
- **Drag** a bar to move it, **pull** its right edge to extend it; linked items
  follow. One “Undo” click if needed.
- **Prepare** sets up everything that can be deduced (existing items linked,
  case and challenge created, sessions placed); with “Automatic launch” ticked,
  the campaign launches by itself on its date. **Launch the campaign** first
  shows what will happen (who can play, start moved forward): you choose
  “Launch” or “Cancel”, then a report says who is enrolled and who is left out.

> The cockpit opens on **This week in the plan**: the running campaign, who has
> not played yet (Nudge), who has progressed (Praise).`,
    },
  },
  {
    id: "actions-entrainement",
    icon: "ListChecks",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Comprendre le plan : les types", en: "Understanding the plan: the types" },
    body: {
      fr: `## Ce que vous posez dans le plan

Le **plan**, c'est ce que vous posez sur le calendrier de votre équipe pour
faire progresser une compétence. La **campagne** est l'enveloppe ;
**challenge**, **parcours**, **sessions** et **évaluation** sont les briques
qu'elle contient — chacune peut aussi vivre seule. Le **tournoi** est un
événement à part.

Dans **Pilot › Planifier**, le bouton **« ? Comprendre le plan »** ouvre la
fiche de chaque type et le cycle de vie ; chaque type a son onglet (**Campagnes**,
**Challenges**, **Tournois**, **Parcours**). En voici l'essentiel.

## Les six types

- **Campagne** — un enjeu, des personnes, des dates, un objectif chiffré. Elle
  monte un cas, souvent un challenge, des sessions à un rythme choisi, et mesure
  le niveau avant / après. *Quand* : un objectif à 4–6 semaines. *Comment* :
  partez d'une situation (« Que voulez-vous obtenir ? ») ou tracez une période
  sur le plan → ajustez personnes, dates, niveau visé → **Préparer**.
- **Challenge** — une compétition sur un simulateur avec objectif et
  classement. *Quand* : de l'émulation sur un geste précis, 1 à 3 semaines.
  *Comment* : « Ajouter au plan › Challenge » (ou depuis une campagne) → titre,
  simulateur, objectif, dates, participants → **Activer**.
- **Parcours** — des phases à valider, chacune sur un simulateur, avec une
  échéance et parfois un certificat. *Quand* : structurer une montée en
  compétences sur plusieurs semaines. *Comment* : choisissez un modèle Kestio →
  personnes et fenêtre → **Assigner**.
- **Évaluation** — cinq parties courtes, une par phase de vente, pour situer le
  niveau. *Quand* : au démarrage, à une arrivée, avant / après un cycle.
  *Comment* : « Ajouter au plan › Évaluation » → personnes et fenêtre →
  **Planifier**.
- **Sessions** — un rythme de parties (1 à 5 par semaine) pour une personne ou
  l'équipe, sans campagne autour. *Comment* : tracez une période sur une ligne →
  choisissez le rythme → **Créer**.
- **Tournoi** — championnat ou élimination directe, avec cagnotte. *Quand* : un
  événement fort et ponctuel.

## Le cycle de vie d'une campagne

1. **Brouillon** — posée, encore modifiable en tout ; personne n'est prévenu.
2. **Planifiée** — « Préparer » a monté son contenu ; si « Lancement
   automatique » est cochée, elle partira seule à sa date de début, sinon vous
   la lancez vous-même. « Lancer la campagne » est possible à tout moment :
   aperçu, puis « Lancer » ou « Annuler ».
3. **En cours** — les participants ont leurs sessions, le challenge tourne, le
   niveau de départ est mesuré. La fin peut se prolonger d'une semaine.
4. **Terminée** — niveau d'arrivée mesuré, verdict calculé (atteint, partiel,
   non atteint), **bilan** disponible (PDF, envoi par mail).

Challenge, parcours et sessions suivent la même logique en plus simple
(brouillon → actif → terminé).

> Règle du plan : tout geste qui écrit à des gens montre d'abord un aperçu,
> puis vous choisissez « Lancer » ou « Annuler ». Rien ne part tant que vous
> n'avez pas confirmé.`,
      en: `## What you place in the plan

The **plan** is what you place on your team's calendar to grow a skill. The
**campaign** is the envelope; **challenge**, **path**, **sessions**
and **assessment** are the building blocks it holds — each can also live on
its own. The **tournament** is a separate event.

In **Pilot › Planning**, the **“? Understand the plan”** button opens each type's
sheet and the life cycle; each type has its own tab (**Campaigns**,
**Challenges**, **Tournaments**, **Paths**). Here is the essence.

## The six types

- **Campaign** — a goal, people, dates, a numeric target. It sets up a case,
  often a challenge, sessions at a chosen pace, and measures the level before /
  after. *When*: a 4–6-week goal. *How*: start from a situation (“What do you
  want to achieve?”) or draw a period on the plan → adjust people, dates,
  target level → **Prepare**.
- **Challenge** — a competition on a simulator with a goal and a leaderboard.
  *When*: momentum on one precise move, 1 to 3 weeks. *How*: “Add to plan ›
  Challenge” (or from a campaign) → title, simulator, goal, dates,
  participants → **Activate**.
- **Path** — phases to validate, each on a simulator, with a deadline and
  sometimes a certificate. *When*: structure a skills climb over several
  weeks. *How*: pick a Kestio template → people and window → **Assign**.
- **Assessment** — five short games, one per sales phase, to place the level.
  *When*: at the start, when someone joins, before / after a cycle. *How*: “New
  action › Assessment” → people and window → **Schedule**.
- **Sessions** — a pace of games (1 to 5 a week) for a person or the team,
  without a campaign. *How*: draw a period on a row → choose the pace →
  **Create**.
- **Tournament** — league or knockout, with a prize pool. *When*: a strong,
  one-off event.

## A campaign's life cycle

1. **Draft** — placed, still fully editable; nobody is notified.
2. **Planned** — “Prepare” built its content; if “Automatic launch” is ticked,
   it launches by itself on its start date, otherwise you launch it yourself.
   “Launch the campaign” is possible at any time: preview, then “Launch” or
   “Cancel”.
3. **Running** — participants have their sessions, the challenge runs, the
   starting level is measured. The end can be extended by a week.
4. **Done** — arrival level measured, verdict computed (achieved, partial,
   not reached), **report** available (PDF, email).

Challenge, path and sessions follow the same logic, simpler (draft → active →
done).

> Plan rule: any action that writes to people shows a preview first, then
> you choose “Launch” or “Cancel”. Nothing goes out until you confirm.`,
    },
  },
  {
    id: "campagnes",
    icon: "Target",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Lancer une campagne", en: "Launching a campaign" },
    body: {
      fr: `## Partir de votre situation, pas du catalogue

Une **campagne** répond à un enjeu : intégrer un nouveau commercial, tenir les
prix, remettre l'équipe en mouvement. Vous choisissez la situation ; l'action
se compose ensuite.

L'onglet **Planifier › Campagnes** (ou « Ajouter au plan › Campagne ») présente trois familles :

- **Démarrer** — mettre l'équipe en mouvement (lancer l'outil, faire monter un
  arrivant).
- **Progresser** — débloquer un résultat précis (rendez-vous, prix, closing,
  réclamations).
- **Ancrer** — installer la durée (certifier, préparer et débriefer ses vrais
  rendez-vous).

Chaque situation annonce ce qu'elle va monter : **un challenge** (une
compétence, un simulateur), **un parcours** (plusieurs phases) ou une **mise en
route** (rien à créer, on active l'existant).

## Le comment

1. Cliquez la situation qui correspond à votre enjeu.
2. Le **cadrage** arrive pré-rempli : nom, personnes concernées, échéance. Deux
   clics suffisent si tout convient.
3. Montez le contenu **sans quitter la campagne**. Chaque brique propose
   d'abord ce qui existe déjà sur votre compte — vous ne créez que ce qui
   manque.
4. Quand tout est prêt, **lancez**. La campagne passe en actif et son cadrage se
   fige : c'est le geste qui clôt la préparation. Le lancement est refusé tant
   qu'une brique obligatoire n'est pas montée.

## Prévenir l'équipe reste votre geste

Le lancement **n'envoie aucun message**. Vos collaborateurs sont prévenus quand
**vous** les invitez, depuis la brique **Inviter l'équipe** — au moment que vous
jugez le bon, avec le mot que vous écrivez. Chacun le reçoit selon ses propres
préférences de notification.

> Tant que la campagne n'est pas lancée, tout reste modifiable. Après le
> lancement, le nom et l'échéance se figent — mais vous pouvez toujours ajouter
> ou retirer des participants.`,
      en: `## Start from your situation, not from a catalogue

A **campaign** answers a real need: onboarding a new rep, holding prices,
getting the team moving again. You pick the situation; the programme is built
from there.

The **Planning › Campaigns** tab (or “Add to plan › Campaign”) shows three families:

- **Get started** — get the team moving (launch the tool, ramp up a newcomer).
- **Progress** — unlock a specific result (meetings, price, closing, complaints).
- **Make it last** — build the habit (certify, prepare and debrief real meetings).

Each situation states what it will set up: **a challenge** (one skill, one
simulator), **a path** (several phases) or an **activation** (nothing to build).

## How to

1. Click the situation that matches your need.
2. The **setup** arrives pre-filled: name, people involved, deadline. Two clicks
   are enough when it all fits.
3. Build the programme **without leaving the campaign**. Each step first offers
   what already exists on your account — you only create what's missing.
4. When it's ready, **launch**. The campaign goes live and its setup is locked:
   this is the gesture that closes the preparation. Launching is refused while a
   required step is still missing.

## Telling your team stays your call

Launching **sends nothing**. Your team members are notified when **you** invite
them, from the **Invite the team** step — at the moment you judge right, with the
words you write. Each of them receives it according to their own notification
preferences.

> Until launch, everything stays editable. After launch the name and deadline
> are locked — but you can still add or remove participants.`,
    },
  },
  {
    id: "creer-offres",
    icon: "Package",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Créer vos offres", en: "Create your offers" },
    body: {
      fr: `## Vos offres

Une **offre** décrit un produit ou un service de votre entreprise. C'est ce que le collaborateur va vendre ou défendre face au simulateur.

## Le comment

1. Allez dans **Mon compte → Offres**.
2. Cliquez sur **Nouvelle offre**.
3. Renseignez le **nom**, la **description**, les **bénéfices** et les **arguments clés**.
4. Vous pouvez utiliser **« Créer avec l'IA »** : décrivez votre offre en quelques mots, l'assistant rédige une première version que vous ajustez.

> Plus la description est riche (bénéfices, objections fréquentes, prix), plus le feedback de fin de session sera pertinent.`,
      en: `## Your offers

An **offer** describes a product or service of your company. It is what the collaborator will sell or defend against the simulator.

## How to

1. Go to **My account → Offers**.
2. Click **New offer**.
3. Fill in the **name**, **description**, **benefits** and **key arguments**.
4. You can use **"Create with AI"**: describe your offer in a few words and the assistant drafts a first version for you to adjust.

> The richer the description (benefits, common objections, pricing), the more relevant the end-of-session feedback.`,
    },
  },
  {
    id: "creer-personae",
    icon: "Users",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Créer vos personae", en: "Create your personas" },
    body: {
      fr: `## Vos personae

Un **persona** est le profil type d'un de vos clients. C'est lui que le simulateur IA incarne pendant la session.

## Les 4 combinaisons

Un persona se décline selon deux axes :

- **B2B** (interlocuteur professionnel) ou **B2C** (particulier),
- **Sales** (vente) ou **Customer Care** (service client).

Selon la combinaison, l'assistant vous demande les champs pertinents (fonction, profil décisionnel, points de douleur, style d'expression, attente principale…).

## Le comment

1. Allez dans **Mon compte → Personae**.
2. Cliquez sur **Nouveau persona** et choisissez la combinaison.
3. Complétez les champs proposés — ou lancez **« Créer avec l'IA »**.
4. Réglez la **personnalité** (style, niveau de maturité, pression interne) pour un comportement plus réaliste.`,
      en: `## Your personas

A **persona** is the typical profile of one of your clients. It is the role the AI simulator plays during the session.

## The 4 combinations

A persona is defined along two axes:

- **B2B** (professional contact) or **B2C** (individual),
- **Sales** or **Customer Care**.

Depending on the combination, the assistant asks for the relevant fields (role, decision-making profile, pain points, expression style, main expectation…).

## How to

1. Go to **My account → Personas**.
2. Click **New persona** and choose the combination.
3. Fill in the suggested fields — or launch **"Create with AI"**.
4. Set the **personality** (style, maturity level, internal pressure) for more realistic behaviour.`,
    },
  },
  {
    id: "configurer-contextes",
    icon: "Layers",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Configurer des contextes supplémentaires", en: "Configure additional contexts" },
    body: {
      fr: `## Les contextes

Le **contexte** décrit la situation de l'échange (prise de rendez-vous, closing, réclamation, renouvellement…). BizCoach fournit déjà des contextes prêts à l'emploi ; vous pouvez en **ajouter des vôtres**.

## Le comment

1. Allez dans **Mon compte → Contextes**.
2. Cliquez sur **Nouveau contexte**, ou **« Créer avec l'IA »**, ou **« Importer un document »** pour extraire un contexte d'un fichier existant.
3. Décrivez la situation, les enjeux et les éventuelles contraintes.
4. Le contexte devient sélectionnable lors de la création d'un **cas de simulation**.

> Les contextes standards suffisent pour démarrer : n'ajoutez les vôtres que pour coller à des situations spécifiques à votre activité.`,
      en: `## Contexts

The **context** describes the situation of the exchange (booking a meeting, closing, complaint, renewal…). BizCoach already provides ready-to-use contexts; you can **add your own**.

## How to

1. Go to **My account → Contexts**.
2. Click **New context**, or **"Create with AI"**, or **"Import a document"** to extract a context from an existing file.
3. Describe the situation, the stakes and any constraints.
4. The context becomes selectable when creating a **simulation case**.

> The standard contexts are enough to get started: add your own only to match situations specific to your business.`,
    },
  },
  {
    id: "ajouter-criteres",
    icon: "ClipboardCheck",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Ajouter des critères d'évaluation", en: "Add evaluation criteria" },
    body: {
      fr: `## Les critères d'évaluation

Les **critères d'évaluation** définissent ce sur quoi une session est notée (découverte du besoin, traitement des objections, closing…). Ils pilotent le **score** et le **feedback** de fin de session.

## Le comment

1. Ouvrez un **simulateur** (ou passez par la configuration de vos cas).
2. Consultez les critères par défaut : ils couvrent déjà les fondamentaux de l'échange.
3. **Ajoutez** ou ajustez des critères pour refléter votre méthode commerciale.
4. Vous pouvez extraire automatiquement des critères depuis un document via l'**IA**.

> Gardez un nombre raisonnable de critères clairs : c'est plus lisible pour le collaborateur et plus fiable pour la notation.`,
      en: `## Evaluation criteria

**Evaluation criteria** define what a session is scored on (needs discovery, objection handling, closing…). They drive the **score** and the end-of-session **feedback**.

## How to

1. Open a **simulator** (or go through your case configuration).
2. Review the default criteria: they already cover the fundamentals of the exchange.
3. **Add** or adjust criteria to reflect your sales methodology.
4. You can automatically extract criteria from a document using **AI**.

> Keep a reasonable number of clear criteria: it is more readable for the collaborator and more reliable for scoring.`,
    },
  },
  {
    id: "creer-collaborateur",
    icon: "UserPlus",
    allowedProfiles: ["ORG", "MNG", "RH"],
    title: { fr: "Créer un collaborateur", en: "Add a collaborator" },
    body: {
      fr: `## Inviter votre équipe

Vos collaborateurs sont les utilisateurs qui vont s'entraîner. Chacun reçoit une **invitation par e-mail** et se connecte sans mot de passe (lien magique).

## Le comment

1. Allez dans **Mon compte → Collaborateurs**.
2. Cliquez sur **Ajouter un collaborateur**.
3. Renseignez **prénom, nom, e-mail**, le **profil** (rôle) et le **manager** de rattachement.
4. Validez : l'invitation part automatiquement. Le collaborateur apparaît « invité » tant qu'il ne s'est pas connecté.

## Consulter et modifier une fiche

- **Cliquez sur la ligne** d'un collaborateur : sa fiche s'ouvre dans une fenêtre que vous pouvez agrandir, avec son statut et ses actions (inviter, renvoyer le lien, désactiver…).
- **Un seul champ à corriger ?** Double-cliquez dessus, modifiez, puis ✓ pour enregistrer ou ✕ pour annuler.
- **Plusieurs champs ?** Cliquez sur **Modifier la fiche**, puis **Enregistrer**.
- **Licences** : ajoutez-les ou retirez-les directement depuis la fiche, sans passer en modification.

## Deux langues par collaborateur

- **Langue d'affichage** (français ou anglais) : celle de l'application et des e-mails.
- **Langue d'entraînement** : celle dans laquelle il s'entraîne — scénario, conversation avec le simulateur, débrief et critères d'évaluation. Une trentaine de langues au choix. Par défaut, elle suit la langue d'affichage.

Réglez-la à la création ou dans la fiche. Chacun peut aussi la changer lui-même dans **Mon profil**. Vous relisez les parties de vos collaborateurs dans **votre** langue d'affichage, quelle que soit la leur.

- **Bénéficiaire (BNF)** : s'entraîne et consulte son historique.
- **Manager (MNG)** : suit son équipe, crée des challenges, gère les fiches et les simulateurs de ses collaborateurs (sans acheter de licences).
- **Organisateur (ORG)** : administre tout le compte.
- **RH (RH)** : gère les collaborateurs (fiches, profils, simulateurs, invitations), les licences et les factures.
- **Gestionnaire (GES)** : gère les licences et les factures, attribue les simulateurs, active ou désactive les comptes.`,
      en: `## Invite your team

Your collaborators are the users who will train. Each receives an **e-mail invitation** and logs in without a password (magic link).

## How to

1. Go to **My account → Collaborators**.
2. Click **Add a collaborator**.
3. Fill in **first name, last name, e-mail**, the **profile** (role) and the assigned **manager**.
4. Confirm: the invitation is sent automatically. The collaborator shows as "invited" until they log in.

## View and edit a profile

- **Click a collaborator's row**: their profile opens in a resizable window, with their status and actions (invite, resend the link, deactivate…).
- **Just one field to fix?** Double-click it, edit, then ✓ to save or ✕ to cancel.
- **Several fields?** Click **Edit profile**, then **Save**.
- **Licences**: assign or remove them straight from the profile, without switching to edit mode.

## Two languages per collaborator

- **Display language** (French or English): the language of the app and the e-mails.
- **Training language**: the language they train in — scenario, conversation with the simulator, debrief and evaluation criteria. About thirty languages to choose from. By default it follows the display language.

Set it when adding a collaborator or in their profile. Everyone can also change their own in **My profile**. You review your collaborators' sessions in **your** display language, whatever theirs is.

- **Beneficiary (BNF)**: trains and reviews their own history.
- **Manager (MNG)**: tracks their team, creates challenges, manages their team members' profiles and simulators (without buying licences).
- **Organiser (ORG)**: administers the whole account.
- **HR (RH)**: manages collaborators (profiles, roles, simulators, invitations), licences and invoices.
- **Finance manager (GES)**: manages licences and invoices, assigns simulators, activates or deactivates accounts.`,
    },
  },
  {
    id: "piloter-licences",
    icon: "KeyRound",
    allowedProfiles: ["ORG", "RH", "GES"],
    title: { fr: "Piloter mes licences", en: "Manage my licences" },
    body: {
      fr: `## Les licences

Une **licence** donne à un collaborateur l'accès aux simulateurs, dans la limite d'un **quota de sessions**. Vous répartissez vos licences entre les membres de votre équipe.

## Le comment

1. Allez dans **Mon compte → Licences** (ou l'onglet de gestion associé).
2. Visualisez les licences **disponibles** et **attribuées**.
3. **Attribuez** ou **retirez** une licence à un collaborateur : ouvrez sa fiche (clic sur sa ligne dans l'onglet Collaborateurs), section **Licences**.
4. Surveillez la **consommation de sessions** : un indicateur en haut de l'écran affiche le quota restant.

> Besoin de sessions supplémentaires ? Voir la section **Facturation & achat de packs**.`,
      en: `## Licences

A **licence** gives a collaborator access to the simulators, within a **session quota**. You allocate your licences among your team members.

## How to

1. Go to **My account → Licences** (or the related management tab).
2. See **available** and **assigned** licences.
3. **Assign** or **remove** a licence for a collaborator: open their profile (click their row in the Collaborators tab), **Licences** section.
4. Monitor **session consumption**: an indicator at the top of the screen shows the remaining quota.

> Need more sessions? See the **Billing & buying packs** section.`,
    },
  },
  {
    id: "studio",
    icon: "Dumbbell",
    allowedProfiles: ["ORG", "MNG", "GES", "RH", "BNF"],
    title: { fr: "Le studio d'entraînement", en: "The training studio" },
    body: {
      fr: `## Votre espace de pratique

L'onglet **Entraînement** rassemble tout ce qui concerne votre pratique : où vous
en êtes, ce que vous avez en cours, et de quoi lancer une partie.

- **Quatre repères** en haut : vos sessions de la semaine, votre série en cours,
  votre score moyen, votre temps d'entraînement. Ils se mettent à jour après
  chaque partie.
- **Vos engagements** : vos sessions de la semaine et le parcours que vous suivez.
  C'est ici que vous reprenez ce qui est commencé.
- **Le studio** : sept simulateurs, chacun avec ce qu'il vous fait travailler.
  Décrocher le rendez-vous, comprendre le besoin, tenir le prix…

## Le comment

1. Ouvrez **Entraînement** dans la barre du haut.
2. Regardez vos sessions : elles ciblent ce sur quoi vous devez progresser.
3. Choisissez un simulateur et lancez votre partie — en voix ou en texte.
4. L'onglet **Historique** garde tout : score, feedback détaillé, et de quoi
   rejouer une situation.

> Une partie dure le temps d'un vrai échange. Mieux vaut deux sessions courtes
> dans la semaine qu'une longue tous les mois : c'est la régularité qui fait
> progresser, et c'est elle que votre série mesure.`,
      en: `## Your practice space

The **Training** tab gathers everything about your practice: where you stand,
what you have going, and what you need to start a game.

- **Four markers** at the top: your sessions this week, your current streak,
  your average score, your training time. They refresh after every game.
- **Your commitments**: this week's sessions and the path you're following. This
  is where you pick up what you started.
- **The studio**: seven simulators, each with what it makes you practise. Book
  the meeting, uncover the need, hold the price…

## How to

1. Open **Training** in the top bar.
2. Look at your sessions: they target what you need to improve.
3. Pick a simulator and start your game — by voice or text.
4. The **History** tab keeps everything: score, detailed feedback, and what you
   need to replay a situation.

> A game lasts about as long as a real conversation. Two short sessions a
> week beat one long one a month: regularity is what makes you progress, and
> that's what your streak measures.`,
    },
  },
  {
    id: "simulateurs-coachs",
    icon: "Bot",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Les simulateurs et les coachs", en: "Simulators and coaches" },
    body: {
      fr: `## Deux familles d'agents IA

BizCoach propose deux types d'agents, complémentaires.

### Les simulateurs (l'entraînement)

Le simulateur **incarne votre client** et vous répond en temps réel, à la voix. Selon les cas configurés, il joue une situation de **vente**, de **négociation**, de **service client**, ou des méthodes structurées (**MEDDIC**, **Discovery**, **Pitch**…). À la fin, vous obtenez un **score** et un **feedback** critère par critère.

### Les coachs (la préparation et le débrief)

Les **coachs** vous accompagnent autour de la simulation :

- **Coach Prépa** : vous aide à préparer un rendez-vous réel (arguments, objections anticipées) et peut générer un cas d'entraînement sur mesure.
- **Coach Debrief / Sales Coach** : analyse un rendez-vous réel (enregistrement ou transcription) et en tire des axes de progrès.

## Où les trouver ?

- Les **coachs** sont sur l'**Accueil**, dans le bloc **Sessions Live** (« Préparer mon RDV », « Débriefer mon RDV »).
- Les **simulateurs** sont sur l'**Accueil**, dans le bloc **Simulateurs d'entraînement** (une carte par simulateur).

## Quand les utiliser ?

- **Avant** un vrai rendez-vous → Coach Prépa. **Après** → Coach Debrief.
- **Pour monter en compétence** de façon répétée → les simulateurs d'entraînement.

> Choisissez le simulateur ou le coach depuis l'**Accueil**, puis lancez la session.`,
      en: `## Two families of AI agents

BizCoach offers two complementary types of agents.

### Simulators (training)

The simulator **plays your client** and answers you in real time, by voice. Depending on the configured cases, it runs a **sales**, **negotiation** or **customer service** situation, or structured methods (**MEDDIC**, **Discovery**, **Pitch**…). At the end you get a **score** and criterion-by-criterion **feedback**.

### Coaches (preparation and debrief)

**Coaches** support you around the simulation:

- **Prep Coach**: helps you prepare a real meeting (arguments, anticipated objections) and can generate a tailored training case.
- **Debrief Coach / Sales Coach**: analyses a real meeting (recording or transcript) and draws out areas for improvement.

## Where to find them

- **Coaches** are on the **Home** page, in the **Live Sessions** block ("Prepare my meeting", "Debrief my meeting").
- **Simulators** are on the **Home** page, in the **Training simulators** block (one card per simulator).

## When to use them

- **Before** a real meeting → Prep Coach. **After** → Debrief Coach.
- **To build skills** through repeated practice → the training simulators.

> Pick a simulator or a coach from the **Home** page, then start the session.`,
    },
  },
  {
    id: "s-entrainer",
    icon: "Mic",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "S'entraîner sur un simulateur", en: "Train on a simulator" },
    body: {
      fr: `## C'est quoi ?

S'entraîner, c'est mener une **conversation vocale en temps réel** avec un client joué par l'IA, sur l'un de vos cas. Vous parlez, il vous répond, exactement comme dans un vrai entretien — puis vous êtes noté.

## Où lancer une session ?

Sur l'**Accueil**, bloc **Simulateurs d'entraînement** : cliquez sur la carte du simulateur voulu (ou son bouton **▶**).

## Lancer une session — pas à pas

1. Depuis l'**Accueil**, choisissez un **simulateur**.
2. Sur la page de présentation, cliquez **S'entraîner**.
3. Sélectionnez le **cas de simulation** (offre + persona + contexte) et le **niveau de difficulté**.
4. Autorisez votre **micro** : la session est **vocale**, vous parlez comme dans un vrai échange.
5. Menez l'entretien avec le client IA jusqu'à la conclusion.

## Après la session

- Vous recevez un **score global** et le **détail par critère**.
- Un **feedback écrit** met en avant vos points forts et vos axes de progrès.
- La session est enregistrée dans votre **Historique**.

## Dans quelle langue ?

Vous vous entraînez dans votre **langue d'entraînement** : le client IA vous parle dans cette langue, et le débrief comme les critères sont rédigés dans cette langue. Elle suit par défaut la langue d'affichage ; changez-la dans **Mon profil** (bouton **Modifier**, puis **Langue d'entraînement**).

> Conseil : rejouez le même cas plusieurs fois pour mesurer votre progression.`,
      en: `## What is it?

Training means holding a **real-time voice conversation** with an AI-played client, on one of your cases. You speak, it replies — exactly like a real meeting — then you get scored.

## Where to start a session

On the **Home** page, **Training simulators** block: click the card of the simulator you want (or its **▶** button).

## Starting a session — step by step

1. From **Home**, choose a **simulator**.
2. On the presentation page, click **Train**.
3. Select the **simulation case** (offer + persona + context) and the **difficulty level**.
4. Allow your **microphone**: the session is **voice-based**, you speak as in a real exchange.
5. Lead the conversation with the AI client through to the conclusion.

## After the session

- You get an **overall score** and the **breakdown by criterion**.
- Written **feedback** highlights your strengths and areas for improvement.
- The session is saved in your **History**.

## In which language?

You train in your **training language**: the AI client speaks to you in that language, and the debrief and criteria are written in it. It follows your display language by default; change it in **My profile** (**Edit**, then **Training language**).

> Tip: replay the same case several times to measure your progress.`,
    },
  },
  {
    id: "participer-challenge",
    icon: "Trophy",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Participer à un challenge", en: "Join a challenge" },
    body: {
      fr: `## Les challenges

Un **challenge** est une compétition d'entraînement sur une période donnée. Il en existe deux types :

- **intra-entreprise** : entre collègues de votre société,
- **inter-entreprises** : contre d'autres sociétés, sous **pseudo** et **avatar anonymisé**.

## Le comment

1. Ouvrez l'onglet **Challenges**.
2. Choisissez un challenge ouvert et cliquez sur **Rejoindre**.
3. Réalisez les sessions demandées : vos meilleurs scores alimentent le **classement**.
4. Suivez votre position en temps réel et **gagnez des badges** en terminant.

> Pour les challenges inter-entreprises, votre identité reste masquée derrière un pseudo : la compétition est saine et confidentielle.`,
      en: `## Challenges

A **challenge** is a training competition over a set period. There are two types:

- **intra-company**: between colleagues of your company,
- **inter-company**: against other companies, under a **pseudonym** and **anonymised avatar**.

## How to

1. Open the **Challenges** tab.
2. Pick an open challenge and click **Join**.
3. Complete the required sessions: your best scores feed the **leaderboard**.
4. Track your position in real time and **earn badges** by finishing.

> For inter-company challenges, your identity stays hidden behind a pseudonym: the competition is healthy and confidential.`,
    },
  },
  {
    id: "creer-challenge",
    icon: "Flag",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Créer un challenge", en: "Create a challenge" },
    body: {
      fr: `## Animer avec un challenge

En tant que manager ou organisateur, vous créez des challenges pour motiver votre équipe.

## Le comment

1. Dans **Challenges**, cliquez sur **Créer un challenge**.
2. Donnez un **nom**, une **période** (dates de début/fin) et un **objectif** (score cible, nombre de sessions).
3. Choisissez le **cas de simulation** concerné et les **participants** (ou constituez des **équipes**).
4. Publiez : les participants sont notifiés et peuvent rejoindre.

## Intra ou inter ?

- **Intra-entreprise** : vous invitez vos collaborateurs directement.
- **Inter-entreprises** : ouvrez la compétition à d'autres sociétés (participants anonymisés).

> Vous pouvez enregistrer un challenge en **brouillon** et le modifier avant de le lancer.`,
      en: `## Engage with a challenge

As a manager or organiser, you create challenges to motivate your team.

## How to

1. In **Challenges**, click **Create a challenge**.
2. Give it a **name**, a **period** (start/end dates) and a **goal** (target score, number of sessions).
3. Choose the relevant **simulation case** and the **participants** (or set up **teams**).
4. Publish: participants are notified and can join.

## Intra or inter?

- **Intra-company**: you invite your collaborators directly.
- **Inter-company**: open the competition to other companies (anonymised participants).

> You can save a challenge as a **draft** and edit it before launching.`,
    },
  },
  {
    id: "badges",
    icon: "Award",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Motivation : BizCoins, flammes & badges", en: "Motivation: BizCoins, streaks & badges" },
    body: {
      fr: `## Rester motivé

BizCoach transforme l'entraînement en habitude avec plusieurs ressorts — tous fondés sur la **régularité** et la **progression**, jamais sur la note brute d'une session.

### 🔥 Les flammes (ta série)
Une **flamme = une semaine tenue** : tu as atteint ton objectif de sessions de la semaine. Ta **série** est le nombre de **semaines d'affilée** tenues (« 4 semaines 🔥 »). Des **paliers** sont célébrés à **2, 4, 8 et 12 semaines**.

### 🪙 Les BizCoins
Une **monnaie interne** que tu cumules en **t'entraînant et en progressant** (session complétée, objectif atteint, semaine tenue, duel gagné…). Tu peux **miser dans les duels** et **débloquer des cadeaux** dans la boutique de récompenses (menu profil → *Récompenses*, si ton entreprise l'active).

### 🏅 Les badges
Gagnés en franchissant des paliers de régularité et en terminant des challenges. Retrouve-les dans ta **collection**.

> La régularité paie : mieux vaut tenir 2 sessions chaque semaine qu'une longue partie isolée.`,
      en: `## Stay motivated

BizCoach turns training into a habit with several levers — all based on **consistency** and **progress**, never a session's raw score.

### 🔥 Streaks (your flame)
A **flame = a week kept**: you reached your session goal for the week. Your **streak** is the number of **weeks in a row** kept ("4 weeks 🔥"). **Milestones** are celebrated at **2, 4, 8 and 12 weeks**.

### 🪙 BizCoins
An **internal currency** you accumulate by **training and progressing** (completed session, objective reached, week kept, duel won…). You can **stake in duels** and **unlock gifts** in the rewards shop (profile menu → *Rewards*, if your company enables it).

### 🏅 Badges
Earned by hitting consistency milestones and completing challenges. Find them in your **collection**.

> Consistency pays off: keeping 2 sessions every week beats one long isolated session.`,
    },
  },
  {
    id: "historique",
    icon: "History",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Historique & scores", en: "History & scores" },
    body: {
      fr: `## Revoir vos sessions

L'onglet **Historique** rassemble toutes vos sessions passées.

- Consultez le **score global** et le **détail par critère** de chaque session.
- Relisez le **feedback** et, pour les coachs, le fil de la conversation.
- Suivez votre **progression** dans le temps sur un même cas.

> Utilisez l'historique pour identifier vos axes récurrents et cibler vos prochains entraînements.`,
      en: `## Review your sessions

The **History** tab gathers all your past sessions.

- Check the **overall score** and the **breakdown by criterion** of each session.
- Re-read the **feedback** and, for coaches, the conversation thread.
- Track your **progress** over time on the same case.

> Use history to spot your recurring themes and target your next training sessions.`,
    },
  },
  {
    id: "reporting",
    icon: "BarChart3",
    allowedProfiles: ["ORG", "MNG", "RH"],
    title: { fr: "Reporting manager", en: "Manager reporting" },
    body: {
      fr: `## Suivre l'activité de l'équipe

Le **Reporting** (« Dashboard ») donne aux managers une vue d'ensemble de l'activité et de la performance.

- **Sessions complètes / démarrées** par collaborateur et par simulateur.
- **Scores moyens** et évolution dans le temps.
- Repérage des collaborateurs **actifs** et de ceux à **relancer**.

## Le comment

1. Ouvrez l'onglet **Dashboard** (visible pour les profils habilités).
2. Filtrez par période, par équipe ou par simulateur.
3. Exploitez les indicateurs pour vos points d'équipe.

> Un KPI « complètes / démarrées » distingue les sessions réellement terminées et **scorées** des simples démarrages.`,
      en: `## Track your team's activity

**Reporting** ("Dashboard") gives managers an overview of activity and performance.

- **Completed / started sessions** per collaborator and per simulator.
- **Average scores** and their evolution over time.
- Spotting **active** collaborators and those to **follow up**.

## How to

1. Open the **Dashboard** tab (visible to authorised profiles).
2. Filter by period, team or simulator.
3. Use the indicators for your team meetings.

> A "completed / started" KPI distinguishes sessions that were actually finished and **scored** from mere starts.`,
    },
  },
  {
    id: "facturation",
    icon: "CreditCard",
    // Section lue par l'organisateur, le RH et le gestionnaire financier ; seuls l'organisateur et
    // le gestionnaire financier ACHÈTENT (règle `canBuyLicences`, décision Valérie Q3 du
    // 29-30/09/2026). Le texte disait au RH d'acheter : le serveur le lui refuse.
    allowedProfiles: ["ORG", "RH", "GES"],
    title: { fr: "Facturation & achat de packs", en: "Billing & buying packs" },
    body: {
      fr: `## Gérer l'abonnement et les sessions

Vous gérez ici les aspects financiers du compte.

- **Qui achète ?** L'**organisateur** et le **gestionnaire financier** du compte : eux seuls achètent des packs, des licences ou l'abonnement. Le **RH** gère les collaborateurs, attribue les licences disponibles et consulte les factures ; pour un achat, il s'adresse à l'un d'eux.
- **Factures** : retrouvez et téléchargez vos factures.
- **Packs de sessions** : quand le quota est atteint, achetez un **pack** pour ajouter des sessions à votre équipe. Un pack reste utilisable jusqu'à épuisement de ses sessions (ou jusqu'à sa date d'expiration), que l'abonnement soit actif, résilié, en pause ou terminé.
- **Abonnement / licences** : ajustez le nombre de licences selon la taille de votre équipe.

## Le comment

1. Ouvrez **Mon compte → Abonnements et pack** (ou cliquez sur l'indicateur de quota lorsqu'il est bas).
2. Choisissez un **pack** ou ajustez vos **licences**.
3. Finalisez le paiement : les sessions sont créditées automatiquement.

> Un indicateur de sessions restantes s'affiche en haut de l'écran pour anticiper les rechargements.`,
      en: `## Manage subscription and sessions

Here you manage the financial side of the account.

- **Who buys?** The account's **organizer** and **finance manager**: only they buy packs, licences or the subscription. **HR** manages team members, assigns available licences and views invoices; for a purchase, HR asks one of them.
- **Invoices**: find and download your invoices.
- **Session packs**: when the quota is reached, buy a **pack** to add sessions for your team. A pack stays usable until its sessions run out (or until its expiry date), whether the subscription is active, cancelled, paused or ended.
- **Subscription / licences**: adjust the number of licences to match your team size.

## How to

1. Open **My account → Subscriptions & pack** (or click the quota indicator when it is low).
2. Choose a **pack** or adjust your **licences**.
3. Complete the payment: sessions are credited automatically.

> A remaining-sessions indicator is shown at the top of the screen to anticipate top-ups.`,
    },
  },
  {
    id: "seances",
    icon: "CalendarDays",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Mes sessions", en: "My sessions" },
    body: {
      fr: `## Trois sources, une file

Sur votre accueil, le bloc **Mes sessions** vous propose quoi jouer maintenant.
Vous ne réglez rien : chaque ligne vient de l'une de trois sources, et son
étiquette le dit.

- **Équipe** — ce que votre manager ou votre organisateur a mis en place : une
  session de campagne, les parties attendues par un challenge, la phase en cours
  d'un parcours.
- **Évaluation** — la compétence que votre évaluation a fait ressortir comme la
  première à renforcer. Tant que vous n'avez pas passé votre évaluation, c'est
  elle qui vous est proposée.
- **Mes parties** — le critère à renforcer de votre dernière partie : rejouer le
  même cas avec ce critère ciblé.

## Le clic fait ce que la ligne dit

**Lancer** ouvre le simulateur prévu avec le cas prévu ; **Rejouer** relance
votre cas avec le critère ciblé ; **Ouvrir** mène au challenge, au parcours ou à
l'évaluation. Jamais une liste où il faudrait chercher.

## La file se recharge à chaque action

Quand vous jouez, la session se coche (+30 BizCoins) et la suivante arrive,
calculée sur ce que vous venez de faire. Trois sessions faites d'affilée =
**carton plein** (+25), quel que soit le temps mis. Une session d'équipe non
jouée à temps reste « à rattraper » : n'importe quelle partie sur le bon
simulateur la solde.

> Vous venez une fois par semaine ou une fois par mois : la file vous attend,
> toujours à jour. Une session suggérée n'est jamais « non réalisée » — elle est
> remplacée quand une meilleure arrive.`,
      en: `## Three sources, one queue

On your home, the **My sessions** block tells you what to play now. Nothing to
set up: each line comes from one of three sources, and its tag says which.

- **Team** — what your manager or organiser set up: a campaign session, the
  games a challenge expects, the current phase of a path.
- **Assessment** — the skill your assessment found most in need of strengthening. Until you have taken
  your assessment, that is what we suggest.
- **My games** — the criterion to strengthen from your last game: replay the same
  case with that criterion targeted.

## The click does what the line says

**Start** opens the planned simulator with the planned case; **Replay** relaunches
your case with the targeted criterion; **Open** leads to the challenge, the path or
the assessment. Never a list to search through.

## The queue refills after every action

When you play, the session gets ticked (+30 BizCoins) and the next one arrives,
computed from what you just did. Three sessions done in a row = **full queue**
(+25), however long it took. A team session not played on time stays “to catch
up”: any game on the right simulator settles it.

> Whether you come once a week or once a month, the queue is waiting, always up
> to date. A suggested session is never “not completed” — it is replaced when a better
> one comes along.`,
    },
  },
  {
    id: "parcours",
    icon: "GraduationCap",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Parcours & évaluation", en: "Paths & assessment" },
    body: {
      fr: `## Ton évaluation initiale

Dès le départ, un **parcours d'évaluation** te situe : **une session par phase de vente**, sans certificat. Tu gardes la main — tu peux le **lancer, le reporter ou le refuser**. À la fin, tu obtiens ton **diagnostic de compétences** (ton radar par phase), qui oriente tes entraînements.

## Les parcours certifiants

Un manager peut t'assigner un **parcours certifiant** : une suite de **phases** à compléter (un cas imposé, un objectif, un nombre de sessions). En le terminant, tu obtiens une **certification vérifiable** (PDF avec QR code + partage LinkedIn).

> Retrouve tes parcours depuis l'**Accueil** (bloc « Tes parcours ») ou le menu dédié.`,
      en: `## Your initial assessment

From the start, an **assessment path** gauges your level: **one session per sales phase**, no certificate. You stay in control — you can **start, postpone or decline** it. At the end you get your **skills diagnostic** (your radar by phase), which guides your training.

## Certification paths

A manager can assign you a **certification path**: a series of **phases** to complete (an imposed case, an objective, a number of sessions). Completing it earns you a **verifiable certification** (PDF with QR code + LinkedIn sharing).

> Find your paths from the **Home** page ("Your paths" block) or the dedicated menu.`,
    },
  },
  {
    id: "duels",
    icon: "Swords",
    allowedProfiles: ALL_CLIENTS,
    title: { fr: "Les duels", en: "Duels" },
    body: {
      fr: `## Défier un collègue

Un **duel** t'oppose à un collègue en **1 contre 1** sur un **cas imposé aux deux**. Chacun joue sa passe, puis le duel se règle automatiquement.

## Miser & gagner

- Tu peux **miser des BizCoins** : la mise est **bloquée à l'acceptation** et revient au gagnant.
- Possibilité de **doubler la mise**.
- **Le gagnant est celui qui a le plus progressé ou atteint l'objectif** — jamais la note brute.
- Un duel **décliné ou expiré rembourse** la mise.

> Les duels se lancent et se suivent depuis l'espace **Défis**.`,
      en: `## Challenge a colleague

A **duel** pits you against a colleague **1-on-1** on a **case imposed on both**. Each plays their round, then the duel settles automatically.

## Stake & win

- You can **stake BizCoins**: the stake is **locked on acceptance** and goes to the winner.
- You can **double the stake**.
- **The winner is whoever progressed most or reached the objective** — never the raw score.
- A **declined or expired** duel refunds the stake.

> Duels are launched and tracked from the **Challenges** space.`,
    },
  },
  {
    id: "creer-parcours",
    icon: "GraduationCap",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Créer un parcours", en: "Create a path" },
    body: {
      fr: `## Bâtir un parcours

Depuis **Pilot → Planifier → Parcours**, vous créez deux types de parcours :

- **Parcours d'évaluation** : pour situer le niveau (diagnostic, sans certificat).
- **Parcours certifiant** : une montée en compétence débouchant sur une **certification**.

## Les phases

Un parcours est une suite de **phases**. Pour chaque phase, vous définissez : le **simulateur**, un **cas imposé** (offre + persona + contexte), un **objectif**, un **nombre de parties minimum** et la **difficulté**.

## Assigner & suivre

Vous **assignez** le parcours à vos collaborateurs et suivez leur **avancée** (statut par phase) dans **Piloter**.`,
      en: `## Build a path

From **Pilot → Planning → Paths**, you create two kinds of paths:

- **Assessment path**: to gauge level (diagnostic, no certificate).
- **Certification path**: skill-building leading to a **certification**.

## Phases

A path is a series of **phases**. For each phase, you define: the **simulator**, an **imposed case** (offer + persona + context), an **objective**, a **minimum number of games** and the **difficulty**.

## Assign & track

You **assign** the path to your collaborators and track their **progress** (status by phase) in **Steer**.`,
    },
  },
  {
    id: "tournois",
    icon: "Medal",
    allowedProfiles: ["ORG", "MNG"],
    title: { fr: "Créer un tournoi", en: "Create a tournament" },
    body: {
      fr: `## Un format à tours

Un **tournoi** est une compétition **à tours** (façon élimination) sur plusieurs matchs. Il se gère **de bout en bout depuis son bandeau**, dans **Pilot → Planifier → Tournois**.

## Ce que vous configurez

- **Format**, **simulateur**, **difficulté**, **métrique**, **récompense**, **deadline / tour**, **cas joué**.
- **Inviter / retirer des participants**, **éditer** le bandeau (config avant démarrage ; nom & cagnotte à tout moment), **supprimer**.

## Suivre

Vous suivez l'**avancement** (tour courant, matchs, vainqueur) directement sur l'écran du tournoi.`,
      en: `## A round-based format

A **tournament** is a **round-based** competition (elimination style) over several matches. It's managed **end-to-end from its banner**, in **Pilot → Planning → Tournaments**.

## What you configure

- **Format**, **simulator**, **difficulty**, **metric**, **reward**, **deadline / round**, **case played**.
- **Invite / remove participants**, **edit** the banner (config before start; name & prize pool any time), **delete**.

## Tracking

You track **progress** (current round, matches, winner) directly on the tournament screen.`,
    },
  },
];

/** Vrai si le profil est un profil interne KESTIO (voit tout le guide). */
export function isInternalProfile(profileType: ProfileType): boolean {
  return INTERNAL_PROFILES.includes(profileType);
}

/** Section localisée prête à afficher. */
export interface LocalizedGuideSection {
  id: string;
  icon: string;
  title: string;
  body: string;
}

/**
 * Renvoie les sections visibles pour un profil donné, projetées dans la langue
 * demandée. Les profils internes KESTIO voient toutes les sections.
 */
// Ordre LOGIQUE des rubriques du guide (parcours utilisateur), indépendant de
// l'ordre de déclaration : Orientation → S'entraîner → Suivre/Motiver → Concevoir
// → Animer → Piloter → Administrer.
const SECTION_ORDER = [
  // Play (opérationnel)
  "prise-en-main", "studio", "simulateurs-coachs", "s-entrainer", "seances", "parcours",
  "historique", "participer-challenge", "duels", "badges",
  // Pilot (encadrant)
  "cockpit", "plan", "campagnes",
  "creer-offres", "creer-personae", "configurer-contextes", "creer-cas-simulation", "ajouter-criteres",
  "creer-parcours", "creer-challenge", "tournois", "reporting", "creer-collaborateur", "piloter-licences", "facturation",
];
const sectionOrderIndex = (id: string) => { const i = SECTION_ORDER.indexOf(id); return i === -1 ? 999 : i; };

// Espace concerné par chaque rubrique : "play" (opérationnel), "pilot" (encadrant),
// "both" (transverse). Sert à conditionner le guide selon la vue active pour les
// profils qui ont le switcher — un commercial n'a pas à voir le détail des fonctions
// manager.
export type GuideScope = "play" | "pilot" | "both";
const SECTION_SCOPE: Record<string, GuideScope> = {
  "prise-en-main": "both",
  "studio": "play", "simulateurs-coachs": "play", "s-entrainer": "play", "seances": "play", 
  "parcours": "play", "historique": "play", "participer-challenge": "play", "duels": "play", "badges": "play",
  "cockpit": "pilot", "plan": "pilot", "campagnes": "pilot",
  "creer-offres": "pilot", "creer-personae": "pilot", "configurer-contextes": "pilot",
  "creer-cas-simulation": "pilot", "ajouter-criteres": "pilot", "creer-parcours": "pilot",
  "creer-challenge": "pilot", "tournois": "pilot", "reporting": "pilot",
  "creer-collaborateur": "pilot", "piloter-licences": "pilot", "facturation": "pilot",
};
export function guideSectionScope(id: string): GuideScope { return SECTION_SCOPE[id] ?? "both"; }

export function getGuideSectionsForProfile(
  profileType: ProfileType,
  lang: string,
  space?: "play" | "pilot" | null,
): LocalizedGuideSection[] {
  const isEn = lang?.toLowerCase().startsWith("en");
  const internal = isInternalProfile(profileType);
  return GUIDE_SECTIONS.filter(
    (s) => internal || s.allowedProfiles.includes(profileType),
  ).filter(
    (s) => !space || guideSectionScope(s.id) === "both" || guideSectionScope(s.id) === space,
  ).sort((a, b) => sectionOrderIndex(a.id) - sectionOrderIndex(b.id)).map((s) => ({
    id: s.id,
    icon: s.icon,
    title: isEn ? s.title.en : s.title.fr,
    body: isEn ? s.body.en : s.body.fr,
  }));
}
